import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentPermissions, PERMISSION_SCHEMA } from '@/api/agentPermissions';
import type { AgentPermissions, PermissionScope } from '@/api/agentPermissions';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/cn';

interface AgentPermissionsEditorProps {
  userId: string;
  canEdit: boolean;
}

/**
 * Fill in the EFFECTIVE default for every schema action the stored matrix
 * doesn't mention, so the checkboxes reflect what the API actually enforces:
 * absent read = allowed (checked), absent edit/create/publish = denied
 * (unchecked). Saving therefore writes a fully-explicit matrix.
 */
function seedDefaults(stored: AgentPermissions | undefined): AgentPermissions {
  const seeded: AgentPermissions = {};
  for (const scope of Object.keys(PERMISSION_SCHEMA) as PermissionScope[]) {
    seeded[scope] = {};
    for (const action of PERMISSION_SCHEMA[scope]) {
      const storedVal = stored?.[scope]?.[action];
      seeded[scope]![action] = typeof storedVal === 'boolean' ? storedVal : action === 'read';
    }
  }
  return seeded;
}

export function AgentPermissionsEditor({ userId, canEdit }: AgentPermissionsEditorProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<AgentPermissions>({});
  const [status, setStatus] = useState<string | null>(null);

  const SCOPE_LABELS: Record<PermissionScope, string> = {
    ivr: t('agentPermissions.scopeIvr'),
    recordings: t('agentPermissions.scopeRecordings'),
    folders: t('agentPermissions.scopeFolders'),
    voicemail: t('agentPermissions.scopeVoicemail'),
    conference: t('agentPermissions.scopeConference'),
    callers: t('agentPermissions.scopeCallers'),
    auth_lists: t('agentPermissions.scopeAuthLists'),
    contacts: t('agentPermissions.scopeContacts'),
    telephony: t('agentPermissions.scopeTelephony'),
    payments: t('agentPermissions.scopePayments'),
    billing: t('agentPermissions.scopeBilling'),
    shop: t('agentPermissions.scopeShop'),
    courses: t('agentPermissions.scopeCourses'),
    surveys: t('agentPermissions.scopeSurveys'),
    quizzes: t('agentPermissions.scopeQuizzes'),
    points: t('agentPermissions.scopePoints'),
    forum: t('agentPermissions.scopeForum'),
    sms: t('agentPermissions.scopeSms'),
    reports: t('agentPermissions.scopeReports'),
    customers: t('agentPermissions.scopeCustomers'),
    users: t('agentPermissions.scopeUsers'),
  };

  const SCOPE_DESCRIPTIONS: Record<PermissionScope, string> = {
    ivr: t('agentPermissions.scopeIvrDesc'),
    recordings: t('agentPermissions.scopeRecordingsDesc'),
    folders: t('agentPermissions.scopeFoldersDesc'),
    voicemail: t('agentPermissions.scopeVoicemailDesc'),
    conference: t('agentPermissions.scopeConferenceDesc'),
    callers: t('agentPermissions.scopeCallersDesc'),
    auth_lists: t('agentPermissions.scopeAuthListsDesc'),
    contacts: t('agentPermissions.scopeContactsDesc'),
    telephony: t('agentPermissions.scopeTelephonyDesc'),
    payments: t('agentPermissions.scopePaymentsDesc'),
    billing: t('agentPermissions.scopeBillingDesc'),
    shop: t('agentPermissions.scopeShopDesc'),
    courses: t('agentPermissions.scopeCoursesDesc'),
    surveys: t('agentPermissions.scopeSurveysDesc'),
    quizzes: t('agentPermissions.scopeQuizzesDesc'),
    points: t('agentPermissions.scopePointsDesc'),
    forum: t('agentPermissions.scopeForumDesc'),
    sms: t('agentPermissions.scopeSmsDesc'),
    reports: t('agentPermissions.scopeReportsDesc'),
    customers: t('agentPermissions.scopeCustomersDesc'),
    users: t('agentPermissions.scopeUsersDesc'),
  };

  const ACTION_LABELS: Record<string, string> = {
    read: t('agentPermissions.actionRead'),
    edit: t('agentPermissions.actionEdit'),
    create: t('agentPermissions.actionCreate'),
    publish: t('agentPermissions.actionPublish'),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['agentPermissions', userId],
    queryFn: () => agentPermissions.get(userId),
  });

  const baseline = useMemo(() => seedDefaults(data?.permissions), [data]);

  useEffect(() => {
    if (data) setDraft(seedDefaults(data.permissions));
  }, [data]);

  const save = useMutation({
    mutationFn: () => agentPermissions.put(userId, draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agentPermissions', userId] });
      setStatus(t('agentPermissions.permissionsSaved'));
    },
    onError: (err) => setStatus(err instanceof Error ? err.message : t('common.failedToSave')),
  });

  function toggle(scope: PermissionScope, action: string) {
    if (!canEdit) return;
    setStatus(null);
    setDraft((prev) => {
      const next: AgentPermissions = { ...prev };
      next[scope] = { ...(prev[scope] ?? {}) };
      next[scope]![action] = !next[scope]![action];
      return next;
    });
  }

  function setAll(scope: PermissionScope, value: boolean) {
    if (!canEdit) return;
    setStatus(null);
    setDraft((prev) => {
      const next: AgentPermissions = { ...prev };
      const actions: Record<string, boolean> = {};
      for (const a of PERMISSION_SCHEMA[scope]) actions[a] = value;
      next[scope] = actions;
      return next;
    });
  }

  const hasChanges = JSON.stringify(draft) !== JSON.stringify(baseline);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
          {t('agentPermissions.title')}
        </CardTitle>
        <CardDescription>
          {t('agentPermissions.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-slate-400">{t('agentPermissions.loadingPermissions')}</p>
        ) : (
          <>
            <div className="grid gap-4">
              {(Object.keys(PERMISSION_SCHEMA) as PermissionScope[]).map((scope) => (
                <div key={scope} className="rounded-md border border-border">
                  <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-border bg-secondary/30">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{SCOPE_LABELS[scope]}</div>
                      <p className="text-xs text-muted-foreground mt-0.5">{SCOPE_DESCRIPTIONS[scope]}</p>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                          onClick={() => setAll(scope, true)}
                        >
                          {t('agentPermissions.all')}
                        </button>
                        <span className="text-[11px] text-muted-foreground">·</span>
                        <button
                          type="button"
                          className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                          onClick={() => setAll(scope, false)}
                        >
                          {t('agentPermissions.none')}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-0 divide-x divide-border">
                    {PERMISSION_SCHEMA[scope].map((action) => {
                      const checked = !!draft[scope]?.[action];
                      return (
                        <label
                          key={action}
                          className={cn(
                            'flex items-center gap-2 px-4 py-2.5 text-sm flex-1 min-w-[120px] transition-colors',
                            canEdit ? 'cursor-pointer hover:bg-secondary/40' : 'cursor-not-allowed opacity-80',
                            checked && 'bg-primary/5',
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!canEdit}
                            onChange={() => toggle(scope, action)}
                            className="accent-primary"
                          />
                          <span className={cn('text-sm', checked ? 'text-foreground font-medium' : 'text-muted-foreground')}>
                            {ACTION_LABELS[action] ?? action}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {canEdit && (
              <div className="mt-6 flex items-center gap-3">
                <Button
                  type="button"
                  onClick={() => save.mutate()}
                  disabled={!hasChanges || save.isPending}
                >
                  {save.isPending ? t('common.saving') : t('agentPermissions.savePermissions')}
                </Button>
                {hasChanges && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => { setDraft(baseline); setStatus(null); }}
                  >
                    {t('agentPermissions.discardChanges')}
                  </Button>
                )}
                {status && <span className="text-xs text-muted-foreground" role="alert">{status}</span>}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
